import { CoordinateData } from '../../utils/coordinates'
import { Motif } from '../../types/motifs'

export const MAP_MIN_ZOOM = 4
export const MAP_MAX_ZOOM = 9
export const MAP_DEFAULT_ZOOM = 4
export const CLUSTER_CITIES_MIN_ZOOM = 7
/**
 * Character budget of a cluster's city label, sized to fill about two lines of
 * `.marker-cluster-cities` in map.css. Names past it fold into a "+N" counter.
 */
export const CLUSTER_CITIES_MAX_CHARS = 36
export const MAP_DEFAULT_CENTER: [number, number] = [47.1625, 19.5033]

export const MAP_ONLINE_TILE_URL =
  'https://tiles.stadiamaps.com/tiles/stamen_toner/{z}/{x}/{y}{r}.png'
export const MAP_LOCAL_TILE_URL =
  import.meta.env.VITE_LOCAL_MAP_TILE_URL || '/tiles/{z}/{x}/{y}.png'

export type MapSource = 'local' | 'online'
/** Which tile source a build uses. Anything other than 'online' means the self-hosted tiles. */
export const MAP_DEFAULT_SOURCE: MapSource =
  import.meta.env.VITE_MAP_SOURCE === 'online' ? 'online' : 'local'
export const MAP_SOURCE_STORAGE_KEY = 'geographic-map-source'

/** Motif marker icon size, in px. Kept in sync with `.motif-marker-image` in styled.ts. */
export const MOTIF_MARKER_SIZE = 64

/**
 * Motif pins get their own map pane, stacked just above Leaflet's tooltip pane
 * (z-index 650) so a hovered, scaled-up pin covers the settlement labels rather
 * than sliding under them. Pin-vs-pin order within the pane is still handled by
 * `riseOnHover`.
 */
export const MOTIF_MARKER_PANE = 'motif-marker-pane'
export const MOTIF_MARKER_PANE_Z_INDEX = 660

/**
 * Spiderfy layout + animation. We take over the position maths from
 * leaflet.markercluster (see spiderfyAnimation.ts): the pins are placed straight
 * onto their spiral slots and revealed there with a staggered scale-in + fade-in
 * (no travel out from the cluster centre).
 */
export const SPIDERFY = {
  /**
   * Scales the whole footprint, like the plugin's `spiderfyDistanceMultiplier`.
   * 1 == stock layout (same lap count); nudge up for more spread without
   * unwinding the spiral.
   */
  distanceMultiplier: 2,
  /**
   * Spiral only: turns rolled forward and discarded so the innermost *kept* pin
   * lands further out, on the genuine spiral, evenly spaced against its
   * neighbour. Raise this — not `startRadiusOffset` — to clear the cluster badge.
   */
  innerSkip: 3,
  /**
   * Flat px added to every pin's radius after the layout is computed. Keep small:
   * a large value over-spaces the inner turns (the angle step still comes from
   * the pre-offset radius). Mainly there to fine-tune the circle case.
   */
  startRadiusOffset: 8,
  /** Child count at/above which the ring becomes a spiral (plugin default: 9). */
  circleToSpiralAt: 9,
  /** Per-pin delay in the reveal cascade; the innermost pin pops in first. */
  staggerMs: 20,
  /**
   * Hard ceiling on the whole cascade (first pin revealed → last pin revealed).
   * On big clusters `staggerMs × pinCount` would drag, so the effective per-pin
   * delay is squeezed below `staggerMs` once the count would blow this budget.
   */
  maxSpreadMs: 750,
  /** How long a single pin takes to scale + fade in, in ms (driven from JS). */
  transitionMs: 200,
  /** Scale a pin starts at before it pops to full size. */
  startScale: 0.4,
} as const

/**
 * Cluster icon tiers by child count. `size` must match the matching
 * `.marker-cluster--<category>` width in styled.ts.
 */
export const CLUSTER_TIERS = [
  { maxCount: 5, category: 'small', size: 40 },
  { maxCount: 20, category: 'medium', size: 55 },
  { maxCount: 50, category: 'large', size: 70 },
  { maxCount: 100, category: 'xlarge', size: 85 },
  { maxCount: Infinity, category: 'xxlarge', size: 100 },
] as const

export interface MotifMarkerData {
  coordinate: CoordinateData
  motif?: Motif
}

/** A Leaflet marker tagged with the settlement name, so clusters can list their cities. */
export interface CityMarker extends L.Marker {
  cityName?: string
}

/** Class applied to the permanent tooltip shown under standalone (unclustered) motif pins. */
export const MOTIF_MARKER_LABEL_CLASS = 'motif-marker-label'
